import Axios from 'axios';
import { z } from 'zod';

/**
 * Floor Plan & Mud Map — host-side API client.
 *
 * The API calls the positioned anchors on a plan **plan points**, not moisture
 * points: a point is a positioned anchor that a reading happens to reference,
 * and the moisture reading structures are due a rewrite they must survive. The
 * client functions are named to match the API.
 *
 * The drawing document blob is opaque to the host — the editor (`restorize-
 * plan-editor`) owns its native schema. We only validate the envelope
 * (id, revision, timestamps) so a malformed server response fails loudly
 * rather than rendering a broken canvas.
 */

const API_PREFIX = '/api/v2/floor-plans';

/**
 * The plan document. `document` is the editor's serialized drawing — validated
 * only as `unknown` here because the host never interprets it; the editor
 * validates it on `load`. `revision` is the optimistic-concurrency token sent
 * back on `PUT`; a 409 means another client saved first.
 */
export const FloorPlanDocumentSchema = z.object({
  id: z.string(),
  jobId: z.string(),
  revision: z.number(),
  document: z.unknown(),
  createdAt: z.string().nullish(),
  updatedAt: z.string().nullish(),
});

export type FloorPlanDocument = z.infer<typeof FloorPlanDocumentSchema>;

/**
 * The kind of anchor a plan point is. The editor renders moisture points as
 * circles and equipment points as squares, so `kind` must round-trip or
 * equipment pins lose their shape on reload.
 */
export type PlanPointKind = 'moisture' | 'equipment';

/**
 * The drying state the editor maps to a pin colour. Derived server-side from
 * the reading value against the threshold so the screen and the PDF agree.
 * `'unknown'` covers pins with no reading at the selected visit.
 */
export type PlanPointState =
  | 'above-control'
  | 'drying'
  | 'at-dry-standard'
  | 'unknown';

/**
 * A positioned anchor on a plan — **inspection-agnostic**. `roomId` links the
 * point to a room so the host can open the right reading dialog on a `pinTapped`
 * message. `kind` distinguishes moisture pins from equipment pins so the
 * editor can render them differently and round-trip the shape on reload.
 */
export const PlanPointSchema = z.object({
  id: z.string(),
  /**
   * The API calls this `jobFloorPlanId` — matching `FloorPlanPoint`. Naming it
   * `floorPlanId` here made every point fail to parse, and because the array is
   * parsed with `.catch([])` the pins silently vanished instead of erroring.
   */
  jobFloorPlanId: z.string().nullish(),
  jobId: z.string().nullish(),
  /** Which level the pin sits on, so the editor can show only this one's. */
  levelId: z.string().nullish(),
  roomId: z.string().nullish(),
  // `decimal` columns come back as numbers via the entity's transformer, but
  // coerce so a string from any other path cannot empty the whole array.
  x: z.coerce.number(),
  y: z.coerce.number(),
  kind: z.enum(['moisture', 'equipment']),
  label: z.string().nullish(),
  createdAt: z.string().nullish(),
  updatedAt: z.string().nullish(),
});

export type PlanPoint = z.infer<typeof PlanPointSchema>;

/**
 * The reading value + drying state for one point at one site visit. The
 * thresholds live in `floor-plan-svg.service.ts` server-side so the screen and
 * the PDF agree; `state` maps to a pin colour in the editor, so a raw `pinColour`
 * hex is not consumed by the editor. Per the design system, status is a badge
 * and never coloured text alone, so the host renders colour WITH a value label
 * and the editor does the same.
 */
export const PlanReadingSchema = z.object({
  pointId: z.string(),
  reading: z.string().nullish(),
  pinColour: z.string().nullish(),
  state: z.enum(['above-control', 'drying', 'at-dry-standard', 'unknown']).nullish(),
  material: z.string().nullish(),
});

export type PlanReading = z.infer<typeof PlanReadingSchema>;

/**
 * What `GET /floor-plans/:jobId` actually returns: the plan nested under
 * `plan`, alongside its points and background.
 *
 * Deliberately a separate schema. `POST` and `PUT` return the bare plan, so one
 * schema cannot describe both — parsing the GET as a flat plan is what made it
 * throw on `id`/`jobId`/`revision`, leaving the client convinced no plan
 * existed and posting a brand new one on every edit.
 */
export const FloorPlanSnapshotSchema = z.object({
  plan: FloorPlanDocumentSchema,
  /**
   * Lenient for the same reason as `observations`: a point-shape drift must
   * degrade rather than take the whole plan down with it.
   *
   * Per point, though — NOT per array. Catching at the array level meant one
   * unparseable point discarded every pin on the plan with no error anywhere,
   * which is exactly how a field renamed to `floorPlanId` made pins silently
   * stop appearing while the API returned 201 on every create.
   */
  points: z
    .array(PlanPointSchema.passthrough().nullable().catch(null))
    .default([])
    .transform((list) => list.filter((point): point is PlanPoint => point !== null)),
  /**
   * Readings keyed by point id.
   *
   * Accepts both an object and an array of `[id, observation]` pairs: the
   * service builds a JS `Map`, which `JSON.stringify` renders as `{}` unless
   * something converts it first. Never strict here and never required — a
   * reading-shape change must not stop the PLAN loading, which is exactly what
   * happened when this field was schema'd tightly.
   */
  observations: z
    .union([
      z.record(z.unknown()),
      z.array(z.tuple([z.string(), z.unknown()])),
    ])
    .nullish()
    .default({}),
  backgroundSignedUrl: z.string().nullish(),
});

export type FloorPlanSnapshot = z.infer<typeof FloorPlanSnapshotSchema>;

/**
 * The snapshot's observations as the `PlanReading[]` the editor bridge wants.
 *
 * `GET /:jobId` is the only source of readings — there is no `/readings`
 * endpoint, and calling one was returning 404 on every load.
 */
export function snapshotReadings(snapshot: FloorPlanSnapshot): PlanReading[] {
  const raw = snapshot.observations;
  const entries: Array<[string, unknown]> = Array.isArray(raw)
    ? (raw as Array<[string, unknown]>)
    : Object.entries(raw ?? {});
  return entries.map(([pointId, value]) => {
    const observation = (value ?? {}) as {
      value?: string;
      state?: PlanPointState;
    };
    return {
      pointId,
      reading: observation.value ?? null,
      state: observation.state ?? 'unknown',
    };
  });
}


/** A single reading value for a point at one site visit, for the history view. */
export const PlanPointHistoryEntrySchema = z.object({
  inspectionId: z.string(),
  inspectionDate: z.string(),
  reading: z.string().nullish(),
  pinColour: z.string().nullish(),
});

export type PlanPointHistoryEntry = z.infer<typeof PlanPointHistoryEntrySchema>;

export interface CreateFloorPlanBody {
  document: unknown;
  /** Required by the API — `name` is a non-nullable column on the plan. */
  name: string;
}

export interface UpdateFloorPlanBody {
  document: unknown;
  revision: number;
}

export interface CreatePointBody {
  /**
   * Must be a real uuid: the API validates it with `@IsUUID()` and only honours
   * it when `useClientId` is set, so a made-up `pt-...` string is rejected.
   */
  id?: string;
  useClientId?: boolean;
  /** Required — the API checks it names a level that exists in the document. */
  levelId: string;
  roomId?: string | null;
  x: number;
  y: number;
  kind: PlanPointKind;
  /** Required by the API, not optional. */
  label: string;
}

export interface UpdatePointBody {
  roomId?: string | null;
  x?: number;
  y?: number;
  kind?: PlanPointKind;
  label?: string;
}

/**
 * A 409 from `updateFloorPlan` — the revision we loaded is stale because
 * another client saved first. Surfaced to the user as a reload-or-overwrite
 * choice, never a silent retry and never a swallowed error. Carried on the
 * rejected axios response as `response.status === 409`.
 */
export const FLOOR_PLAN_CONFLICT_STATUS = 409;

/**
 * The plan, its points and its readings in one call.
 *
 * Returns the whole snapshot rather than just the plan because it is the only
 * endpoint that serves points and observations — there is no `/points` or
 * `/readings` route to fetch them separately.
 */
export async function getFloorPlan(jobId: string): Promise<FloorPlanSnapshot> {
  const res = await Axios.get(`${API_PREFIX}/${jobId}`);
  // A job with no plan answers 200 with a null body, which is a 404 as far as
  // callers are concerned — they already branch on that.
  if (res.data == null) {
    throw Object.assign(new Error('No floor plan for this job'), { status: 404 });
  }
  return FloorPlanSnapshotSchema.parse(res.data);
}

export async function createFloorPlan(
  jobId: string,
  body: CreateFloorPlanBody
): Promise<FloorPlanDocument> {
  const res = await Axios.post(`${API_PREFIX}/${jobId}`, body);
  return FloorPlanDocumentSchema.parse(res.data);
}

export async function updateFloorPlan(
  jobId: string,
  id: string,
  body: UpdateFloorPlanBody
): Promise<FloorPlanDocument> {
  const res = await Axios.put(`${API_PREFIX}/${jobId}/${id}`, body);
  return FloorPlanDocumentSchema.parse(res.data);
}



/**
 * An uploaded floor plan IMAGE — the old per-inspection photo, not the vector
 * drawing this module is otherwise about.
 *
 * `gcsPath` carries a signed HTTPS URL, not a `gs://` URI. Keeping the field
 * name the v1 route uses is what lets `FloorPlanImageGallery` consume these
 * rows unchanged rather than growing a fourth copy of the lightbox.
 */
export const UploadedPlanImageSchema = z.object({
  id: z.string(),
  inspectionId: z.string(),
  inspectionDate: z.string().nullish(),
  isInitialAttendance: z.boolean().nullish(),
  gcsPath: z.string(),
  fileByteSize: z.coerce.number().nullish(),
  createdOn: z.string().nullish(),
});

export type UploadedPlanImage = z.infer<typeof UploadedPlanImageSchema>;

/**
 * Every floor plan image on the job, oldest visit first.
 *
 * Images are stored per inspection, so the alternative was one request per
 * visit. Parsed per element with `.catch(null)` for the same reason the points
 * array is: one malformed row must not empty the gallery.
 */
export async function listUploads(
  jobId: string
): Promise<UploadedPlanImage[]> {
  const res = await Axios.get(`${API_PREFIX}/${jobId}/uploads`);
  const parsed = z
    .object({
      uploads: z
        .array(UploadedPlanImageSchema.nullable().catch(null))
        .default([]),
    })
    .safeParse(res.data);
  if (!parsed.success) {
    return [];
  }
  return parsed.data.uploads.filter(
    (upload): upload is UploadedPlanImage => upload !== null
  );
}

/** One level of the drawing, rendered server-side as a standalone SVG. */
export const PlanLevelPreviewSchema = z.object({
  id: z.string(),
  name: z.string(),
  order: z.coerce.number(),
  svg: z.string(),
  shapeCount: z.coerce.number().default(0),
  pointCount: z.coerce.number().default(0),
  markingCount: z.coerce.number().default(0),
});

export type PlanLevelPreview = z.infer<typeof PlanLevelPreviewSchema>;

export const PlanPreviewsSchema = z.object({
  planId: z.string().nullish(),
  revision: z.coerce.number().nullish(),
  levels: z.array(PlanLevelPreviewSchema).default([]),
});

export type PlanPreviews = z.infer<typeof PlanPreviewsSchema>;

/**
 * Read-only SVG previews of the drawing, one per level, already sorted by the
 * level's `order`.
 *
 * @param inspectionId the visit whose markings to overlay. Omitted renders the
 * bare drawing.
 *
 * Unlike `getFloorPlan`, a job with no drawing is NOT an error here — it
 * answers with an empty `levels` array, because "no drawing yet" is the
 * ordinary case this powers an empty state for.
 *
 * Strict, and deliberately unlike the lenient `.catch(null)` parsing the rest
 * of this module uses. Everywhere else, degrading to a shorter list is honest:
 * fewer pins is visibly fewer pins. Here it is not — dropping the levels that
 * failed to parse renders "No floor plan drawn yet." over a plan the
 * technician spent an hour on. Throwing puts it in the caller's `isError`
 * branch, which says the previews could not be loaded, which is true.
 */
export async function getPlanPreviews(
  jobId: string,
  inspectionId?: string
): Promise<PlanPreviews> {
  const res = await Axios.get(`${API_PREFIX}/${jobId}/previews`, {
    params: inspectionId ? { inspectionId } : undefined,
  });
  return PlanPreviewsSchema.parse(res.data);
}

export async function createPoint(
  jobId: string,
  planId: string,
  body: CreatePointBody
): Promise<PlanPoint> {
  const res = await Axios.post(`${API_PREFIX}/${jobId}/${planId}/points`, body);
  const parsed = PlanPointSchema.safeParse(res.data);
  return parsed.success ? parsed.data : (res.data as PlanPoint);
}

export async function updatePoint(
  jobId: string,
  planId: string,
  pointId: string,
  body: UpdatePointBody
): Promise<PlanPoint> {
  const res = await Axios.put(
    `${API_PREFIX}/${jobId}/${planId}/points/${pointId}`,
    body
  );
  const parsed = PlanPointSchema.safeParse(res.data);
  return parsed.success ? parsed.data : (res.data as PlanPoint);
}

export async function removePoint(
  jobId: string,
  planId: string,
  pointId: string
): Promise<void> {
  await Axios.delete(`${API_PREFIX}/${jobId}/${planId}/points/${pointId}`);
}

export async function getPointHistory(
  jobId: string,
  planId: string,
  pointId: string
): Promise<PlanPointHistoryEntry[]> {
  const res = await Axios.get(
    `${API_PREFIX}/${jobId}/${planId}/points/${pointId}/history`
  );
  const parsed = z.array(PlanPointHistoryEntrySchema).safeParse(res.data);
  return parsed.success ? parsed.data : (res.data as PlanPointHistoryEntry[]);
}

/**
 * A marking: a damage / remediation annotation recorded against ONE site visit.
 *
 * Its own record rather than a shape in the plan document, because a marking is
 * temporal and the document is not — see
 * `restorize-api/docs/plans/floor-plan-markings.md`.
 */
export const MarkingSchema = z.object({
  id: z.string(),
  jobFloorPlanId: z.string().nullish(),
  inspectionId: z.string(),
  levelId: z.string(),
  points: z.array(z.object({ x: z.coerce.number(), y: z.coerce.number() })),
  /*
   * `containment` belongs here even though the editor's containment tool is
   * newer than this schema. `listMarkings` parses each row with
   * `.catch(null)` and drops the nulls, so a surface this enum does not know
   * makes the marking vanish on reload rather than erroring — the user draws
   * a barrier, gets a 201, and finds it gone. Widening the READ path is safe
   * on its own; the write path follows once the API accepts it.
   */
  surface: z.enum(['floor', 'wall', 'containment']).default('floor'),
  state: z.enum(['affected', 'remediated', 'unaffected']).default('affected'),
  label: z.string().nullish(),
});

export type Marking = z.infer<typeof MarkingSchema>;

/** Mirrors the API's `FloorPlanMarkingSurface` and the editor's `AreaSurface`. */
export type MarkingSurface = Marking['surface'];

/** Mirrors the API's `FloorPlanMarkingState`. */
export type MarkingState = Marking['state'];

export interface CreateMarkingBody {
  inspectionId: string;
  levelId: string;
  points: { x: number; y: number }[];
  surface?: MarkingSurface;
  state?: MarkingState;
  label?: string;
}

export interface UpdateMarkingBody {
  points?: { x: number; y: number }[];
  surface?: MarkingSurface;
  state?: MarkingState;
  label?: string;
}

/** Which visits a copy lands on. Mirrors the API's `MarkingCopyScope`. */
export type MarkingCopyScope = 'thisOnly' | 'thisAndPrevious' | 'thisAndFuture';

export async function listMarkings(
  jobId: string,
  planId: string,
  inspectionId: string
): Promise<Marking[]> {
  const res = await Axios.get(
    `${API_PREFIX}/${jobId}/${planId}/markings/${inspectionId}`
  );
  // Per marking, not per array: one unparseable marking must not discard the
  // rest, the way a per-array catch once silently emptied every plan point.
  const parsed = z
    .array(MarkingSchema.passthrough().nullable().catch(null))
    .default([])
    .safeParse(res.data);
  return parsed.success
    ? parsed.data.filter((marking): marking is Marking => marking !== null)
    : [];
}

export async function createMarking(
  jobId: string,
  planId: string,
  body: CreateMarkingBody
): Promise<Marking> {
  const res = await Axios.post(`${API_PREFIX}/${jobId}/${planId}/markings`, body);
  return MarkingSchema.parse(res.data);
}

export async function updateMarking(
  jobId: string,
  planId: string,
  markingId: string,
  body: UpdateMarkingBody
): Promise<Marking> {
  const res = await Axios.put(
    `${API_PREFIX}/${jobId}/${planId}/markings/${markingId}`,
    body
  );
  return MarkingSchema.parse(res.data);
}

export async function removeMarking(
  jobId: string,
  planId: string,
  markingId: string
): Promise<void> {
  await Axios.delete(`${API_PREFIX}/${jobId}/${planId}/markings/${markingId}`);
}

/**
 * Carry a visit's markings onto others — the "you have already drawn this,
 * start from it" path when a new site visit is added.
 */
export async function copyMarkings(
  jobId: string,
  planId: string,
  body: { inspectionId: string; fromInspectionId?: string; scope?: MarkingCopyScope }
): Promise<Marking[]> {
  const res = await Axios.post(`${API_PREFIX}/${jobId}/${planId}/markings/copy`, body);
  const parsed = z.array(MarkingSchema.passthrough()).safeParse(res.data);
  return parsed.success ? parsed.data : [];
}
